import { useParams, Link } from 'react-router-dom';
import { SEOHead, BreadcrumbNav } from '@/components/feature/SEO';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-20 w-full px-4 md:px-6 py-14">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">
            文章未找到
          </h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const cat = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbs = [
    { name: '首页', url: '/' },
    { name: cat?.name || article.category, url: `/category/${article.category}` },
    { name: article.title, url: `/article/${article.slug}` },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '制服诱惑',
      url: 'https://dressplay.github.io',
    },
    url: `https://dressplay.github.io/article/${article.slug}`,
  };

  return (
    <main className="pt-20">
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://dressplay.github.io/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        breadcrumbs={breadcrumbs}
        schema={articleSchema}
      />

      <BreadcrumbNav items={breadcrumbs} />

      <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-block text-xs text-accent-600 bg-accent-100 px-3 py-1 rounded-full mb-4 hover:bg-accent-200 transition-colors"
            >
              {cat?.name || ''}
            </Link>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
            </div>
          </div>

          <div className="relative w-full aspect-video rounded-lg overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-5"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-background-100 text-xs text-foreground-600 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-12 pt-8 border-t border-background-200">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <div className="relative w-full h-36 overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-xs font-semibold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-400 mt-2">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </main>
  );
}